/*
  MASTER — controlador de acciones de compra.

  Por qué existe
  --------------
  Hasta ahora "comprar" era una sola ruta: buy box -> carrito -> checkout de
  Shopify. Con contraentrega esa ruta deja de ser la única: el cliente colombiano
  abre un formulario COD desde la ficha y el pedido nace ahí, sin pasar por el
  carrito. Y sigue existiendo la ruta de prepago.

  Dos rutas por seis sitios donde hay una CTA (buy box principal, secundaria,
  píldora de PC, sticky móvil, CTA intermedia, CTA final) son doce
  implementaciones si cada botón resuelve su destino por su cuenta. Este archivo
  es el único que sabe cómo se ejecuta una acción de compra; los botones sólo
  declaran cuál quieren con `data-purchase-action`.

  Lo que este archivo NO es
  -------------------------
  No es un segundo motor económico. No calcula Q1/Q2/Q3, no sabe cuánto ahorra
  un pack y no formatea dinero. Esa autoridad es de master-offer.js para la
  proyección y del carrito/pedido real de Shopify para la verdad. Aquí sólo se
  transporta ESTADO (producto, variante, cantidad, nivel) y se dispara una
  ACCIÓN.

  Tampoco es un segundo carrito: `open_cart` y `native_checkout` delegan en
  master-cart.js, que sigue siendo el único dueño de la mutación del carrito.

  Y no toca la configuración de la app de COD. Los ajustes de Releasit son
  globales de tienda —afectan también al tema publicado— así que desde el theme
  se leen y se usan, nunca se escriben.

  El contrato con el proveedor COD
  --------------------------------
  El proveedor observado (Releasit V2) descubre el producto leyendo el
  formulario nativo de Shopify: `input[name="id"]` para la variante y
  `input[name="quantity"]` para la cantidad, dentro de un
  `form[action*="/cart/add"]`. MASTER ya escribe exactamente ahí cuando cambia
  el selector de packs, así que el traspaso es nativo y no hay que inventarse
  ningún puente. Lo único que este archivo garantiza es que el formulario esté
  sincronizado ANTES de abrir el formulario COD, porque el proveedor lo lee en
  ese instante y no vuelve a mirarlo.

  Ver factory/v2/providers/releasit-integration-observation.json para la
  evidencia de cada afirmación de arriba.
*/
(() => {
  if (window.MasterPurchase) return;

  const root = window.MasterTheme?.root || '/';

  /* ------------------------------------------------------------------ *
   * Estado nativo del producto
   * ------------------------------------------------------------------ */

  const productForm = () => document.querySelector('[data-master-product-form]');

  const selectedCard = form => {
    const scope = form?.closest('[data-master-product]') || document;
    return scope.querySelector('[data-pack-card] input:checked')?.closest('[data-pack-card]') || null;
  };

  /*
    La foto de lo que el cliente tiene elegido AHORA. Sale del formulario real,
    no de una variable paralela: si el formulario y esta lectura discrepasen, el
    que manda seguiría siendo el formulario, así que leerlo directamente elimina
    la discrepancia en vez de gestionarla.
  */
  const state = () => {
    const form = productForm();
    if (!form) return null;
    const card = selectedCard(form);
    return {
      productId: Number(form.closest('[data-master-product]')?.dataset.productId || 0) || null,
      variantId: Number(form.querySelector('[data-variant-id]')?.value || 0) || null,
      quantity: Math.max(1, Number(form.querySelector('[data-quantity-input]')?.value || 1)),
      tier: card ? Math.max(1, Number(card.dataset.packQty || 1)) : 1,
      tierTitle: card?.dataset.packTitle || '',
      // Se expone el código del nivel porque el carrito lo aplica al añadir,
      // no porque aquí se decida nada económico con él.
      tierDiscountCode: card?.dataset.packDiscountCode || '',
      available: !form.querySelector('[data-atc]')?.disabled
    };
  };

  /*
    El invariante que hace que todo lo demás sea cierto: la cantidad que el
    cliente ve seleccionada y la cantidad que Shopify (y por tanto el proveedor
    COD) va a leer tienen que ser el mismo número.

    master-product.js ya la escribe al cambiar de tarjeta. Esto no duplica esa
    lógica: la COMPRUEBA justo antes de una acción de compra, que es el único
    momento en que una desincronización se convertiría en un pedido con la
    cantidad equivocada. Devuelve un informe en vez de callarse para que las
    puertas de QA puedan afirmar sobre él.
  */
  const syncNativeProductState = () => {
    const form = productForm();
    if (!form) return { ok: false, reason: 'NO_PRODUCT_FORM' };

    const qtyInput = form.querySelector('[data-quantity-input]');
    const variantInput = form.querySelector('[data-variant-id]');
    if (!qtyInput || !variantInput) return { ok: false, reason: 'NO_NATIVE_INPUTS' };

    const card = selectedCard(form);
    const wanted = card ? Math.max(1, Number(card.dataset.packQty || 1)) : Math.max(1, Number(qtyInput.value || 1));
    const before = Number(qtyInput.value || 0);
    const repaired = before !== wanted;
    if (repaired) qtyInput.value = String(wanted);

    /* Un segundo campo de cantidad en la página rompería el descubrimiento del
       proveedor, que ante varios candidatos elige por heurística y puede
       quedarse con el que no es. Se informa; no se borra nada ajeno. */
    const rivals = [...document.querySelectorAll('input[name="quantity"], .quantity__input, quantity-input input')]
      .filter(node => node !== qtyInput).length;

    return {
      ok: true,
      quantity: wanted,
      variantId: Number(variantInput.value || 0) || null,
      repaired,
      previousQuantity: before,
      competingQuantityInputs: rivals
    };
  };

  /* ------------------------------------------------------------------ *
   * Proveedor COD
   * ------------------------------------------------------------------ *
   *
   * Detección deliberadamente redundante y sin acoplarse a una sola clase CSS
   * volátil: el proveedor renderiza con styled-components, así que sus clases
   * (`sc-iJKPHa`…) cambian en cada build y no sirven como contrato. Lo que sí
   * es estable es el id del botón, el global de runtime y el <dialog>
   * namespaced. Con tres señales independientes, que una cambie degrada el
   * diagnóstico pero no rompe la apertura.
   */
  /*
    Cómo se aparta el botón del proveedor sin pelearse con sus estilos.

    Lo primero que se intentó fue recortar el propio botón, con estilos en línea
    y luego con una hoja de estilo de mayor especificidad. Las dos cosas
    fallaron: el proveedor reafirma su anchura, y como el recorte necesitaba
    sacarlo del flujo, un botón absoluto al 100 % de ancho generaba 1027 px de
    scroll horizontal. Peor el remedio que la enfermedad.

    Lo que sí funciona es no tocar el botón: se le pone alrededor un hueco de
    1×1 que MASTER sí controla, y se recorta el hueco. Da igual lo ancho que el
    proveedor crea que es su botón, porque el que decide el tamaño de la caja es
    el padre. Medido: botón 0×0, hueco 1×1, desbordamiento horizontal 0.
  */
  const SLOT_ATTR = 'data-master-cod-slot';
  const FLOATING_STYLE_ID = 'master-purchase-cod-floating';

  const slotFor = button => {
    const existing = button.parentElement?.hasAttribute?.(SLOT_ATTR) ? button.parentElement : null;
    if (existing) return existing;
    const slot = document.createElement('span');
    slot.setAttribute(SLOT_ATTR, '');
    slot.setAttribute('aria-hidden', 'true');
    slot.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%)';
    button.parentElement.insertBefore(slot, button);
    slot.appendChild(button);
    return slot;
  };

  const RELEASIT = {
    id: 'releasit',
    runtime: () => window._rsiV2 || null,
    button: () => document.getElementById('rsi_buy_now_button'),
    dialog: () => document.querySelector('dialog._rsi_main_form_dialog_element'),
    isOpen() { const d = this.dialog(); return Boolean(d && d.hasAttribute('open')); },

    detect() {
      const runtime = this.runtime();
      const button = this.button();
      if (!runtime && !button) return 'NOT_INSTALLED';
      let enabled = null;
      try { enabled = runtime?.getState?.()?.productPage?.isEnabled ?? null; } catch (_) { enabled = null; }
      if (enabled === false) return 'DISABLED';
      if (button) return 'READY';
      if (runtime) return 'AVAILABLE';
      return 'UNKNOWN';
    },

    open() {
      const button = this.button();
      if (!button) return false;
      // El botón del proveedor es el único punto de entrada documentado. Se
      // pulsa el suyo en vez de replicar su lógica de apertura, que es privada.
      button.click();
      return true;
    },

    /*
      El proveedor inyecta su propio botón dentro del formulario de producto,
      con su texto y su estilo. Cuando una CTA de MASTER ya declara que abre el
      formulario COD, ese botón sobra a la vista: serían dos llamadas a la
      acción idénticas, una de ellas sin la marca.

      Se aparta en vez de quitarlo: sigue montado y sigue siendo el punto de
      entrada que pulsamos. `aria-hidden` y `tabindex="-1"` lo sacan del lector
      de pantalla y del tabulador, porque la CTA accesible de verdad es la de
      MASTER, que apunta a la misma acción.
    */
    delegate(button) {
      if (!button) return;
      slotFor(button);
      button.setAttribute('aria-hidden', 'true');
      button.setAttribute('tabindex', '-1');
    },

    /*
      El proveedor puede además pintar su PROPIA barra fija, y en móvil la pone
      abajo del todo — exactamente donde vive la barra sticky de MASTER. Medido
      en el navegador: su botón fijo ocupaba y=786 h=58 y la barra de MASTER
      y=781 h=63. Dos barras superpuestas.

      Cuando la CTA fija de MASTER ya abre el formulario COD, la del proveedor
      es un duplicado sin marca encima de la nuestra, así que se retira. No se
      retira nunca si MASTER no tiene una CTA fija que haga esa acción: en esa
      tienda la barra del proveedor puede ser la única puerta a la
      contraentrega, y quitarla dejaría al comprador sin forma de pedir.

      `rsi_floating` es una clase semántica del proveedor, no un hash de build
      como `sc-iJKPHa`; por eso se puede nombrar. Si aun así cambiase, el
      conflicto volvería a verse y la solución de fondo sigue estando donde
      siempre estuvo: apagar el botón flotante en el Form Designer, que es un
      ajuste del dueño y no del theme.
    */
    standDownFloating() {
      if (document.getElementById(FLOATING_STYLE_ID)) return;
      const style = document.createElement('style');
      style.id = FLOATING_STYLE_ID;
      style.textContent = '#rsi_buy_now_button.rsi_floating{display:none!important}';
      document.head.appendChild(style);
    },

    /*
      ¿Se cerró porque el cliente pidió, o porque se fue? El proveedor guarda la
      respuesta del pedido en su estado, así que se lee de ahí. Ante cualquier
      duda —estado ilegible, forma inesperada— la respuesta es "no completado":
      equivocarse hacia ahí sólo puede hacer que un rescate se ofrezca de más,
      mientras que equivocarse al revés lo silenciaría para siempre.

      Aun así esto no es la única defensa: cuando el pedido se crea, el
      proveedor navega a la página de gracias, y una página que se va no
      dispara nada.
    */
    completed() {
      try { return Boolean(this.runtime()?.getState?.()?.orderResponse); } catch (_) { return false; }
    }
  };

  let provider = RELEASIT;

  const codStatus = () => {
    try { return provider.detect(); } catch (_) { return 'UNKNOWN'; }
  };

  /* ------------------------------------------------------------------ *
   * Ciclo de vida del formulario COD
   * ------------------------------------------------------------------ *
   *
   * El proveedor no emite eventos propios y su <dialog>, aunque es un
   * HTMLDialogElement de verdad, se abre poniendo el atributo `open` desde
   * React en vez de con showModal(), así que los eventos nativos `close` y
   * `cancel` NO se disparan (comprobado, no supuesto). Lo que sí es fiable es
   * el propio atributo: observarlo da abierto/cerrado sin tocar nada interno.
   *
   * Se reemite como eventos de MASTER para que el rescate y la analítica
   * escuchen a MASTER y no al proveedor: el día que el proveedor cambie, este
   * archivo es el único que se entera.
   */
  let watching = null;
  let wasOpen = false;
  let lastClosedAt = 0;

  /*
    Candado de exclusión mutua entre las dos rutas de compra. `checkout()` ya
    tenía el suyo (Commit K); `openCod()` no tenía ninguno, así que dos
    aperturas casi simultáneas podían disparar dos click() sobre el botón del
    proveedor, y un checkout disparado justo mientras el formulario COD está
    abriéndose podía navegar a mitad de esa apertura. La ventana que cubre
    `codOpenInFlight` es la apertura en sí —desde que se pulsa el botón del
    proveedor hasta que su <dialog> confirma `open` (o el respaldo expira)—,
    no todo el tiempo que el formulario COD permanece visible: una vez abierto
    de verdad, el cliente puede seguir navegando la página con normalidad.
  */
  let dialogObserver = null;
  let codOpenInFlight = false;
  let releaseCodOpenLock = null;
  const COD_OPEN_SAFETY_TIMEOUT_MS = 6000;

  const COMPLETED_KEY = 'master-purchase:cod-completed';
  const sessionFlag = {
    get: () => { try { return sessionStorage.getItem(COMPLETED_KEY) === '1'; } catch (_) { return false; } },
    set: () => { try { sessionStorage.setItem(COMPLETED_KEY, '1'); } catch (_) {} }
  };

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  const onDialogToggle = () => {
    const open = provider.isOpen();
    if (open === wasOpen) return;
    wasOpen = open;
    if (open) {
      releaseCodOpenLock?.();
      emit('master:cod-open', { provider: provider.id, state: state() });
      return;
    }
    lastClosedAt = Date.now();
    const completed = provider.completed();
    if (completed) sessionFlag.set();
    emit('master:cod-close', { provider: provider.id, completed, state: state() });
  };

  /*
    El proveedor NO reutiliza su <dialog>: lo saca del DOM al cerrar y monta uno
    nuevo al abrir (comprobado en runtime — el nodo pasa de 1 a 0 y vuelve a 1).
    Eso rompía la liberación del candado: al enlazar con el nodo nuevo,
    `wasOpen` se inicializaba con `provider.isOpen()`, que para entonces ya era
    `true`, así que la transición cerrado -> abierto nunca se observaba y
    `releaseCodOpenLock()` no llegaba a ejecutarse. El candado sólo caía por el
    temporizador de seguridad de 6 s, y durante esos 6 s TODAS las CTA de compra
    quedaban mudas: `open_cod` devolvía ALREADY_IN_FLIGHT y `native_checkout`
    devolvía COD_OPENING. Justo después de cerrar el formulario COD —el momento
    en que el rescate invita a pagar por adelantado— no respondía ningún botón.

    Un nodo recién enlazado no tiene historia observada, así que se arranca en
    `false` y se adopta su estado real llamando al mismo manejador: si ya viene
    abierto, eso ES la transición que faltaba y el candado se libera; si viene
    cerrado, el manejador sale solo y nada cambia.
  */
  const watchLifecycle = () => {
    const dialog = provider.dialog();
    if (!dialog || watching === dialog) return;
    watching = dialog;
    dialogObserver?.disconnect();
    wasOpen = false;
    dialogObserver = new MutationObserver(onDialogToggle);
    dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    onDialogToggle();
  };

  /* ¿Hay alguna CTA de MASTER que ya abra el formulario COD? Entonces el botón
     del proveedor pasa a segundo plano. Si no la hay, se deja como está: en una
     tienda que no use `open_cod` en ninguna parte, el botón de la app es la
     única puerta a la contraentrega y esconderlo sería quitarle al comprador la
     forma de pagar. */
  const delegationActive = () => Boolean(document.querySelector('[data-purchase-action="open_cod"]'));

  /* Sólo cuenta una CTA FIJA de MASTER: es la única que compite por el mismo
     sitio de la pantalla que la barra del proveedor. */
  const stickyDelegationActive = () => Boolean(
    document.querySelector('[data-sticky-cta] [data-purchase-action="open_cod"], [data-desktop-cta] [data-purchase-action="open_cod"]')
  );

  const applyDelegation = () => {
    if (!delegationActive()) return;
    provider.delegate?.(provider.button());
    if (stickyDelegationActive()) provider.standDownFloating?.();
  };

  /* Si el proveedor vuelve a renderizar y saca el botón del hueco, se le vuelve
     a poner. Observa sólo el formulario de producto, no el documento entero:
     es donde el botón aparece y donde puede reaparecer. */
  let formWatched = null;
  const keepDelegated = () => {
    if (!delegationActive()) return;
    const form = productForm();
    if (!form || formWatched === form) return;
    formWatched = form;
    new MutationObserver(applyDelegation).observe(form, { childList: true });
  };

  /* Tanto el <dialog> como el botón se montan cuando al proveedor le apetece,
     así que se espera a que aparezcan en vez de asumir que ya están. */
  const observeForDialog = () => {
    watchLifecycle();
    applyDelegation();
    keepDelegated();
    if (watching && provider.button()) return;
    const mo = new MutationObserver(() => {
      watchLifecycle();
      applyDelegation();
      keepDelegated();
      if (watching && provider.button()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: true });
  };

  /* Enlace acotado con el <dialog> recién montado. El límite es deliberado: si
     el proveedor no lo monta, manda el temporizador de seguridad y la CTA no se
     queda esperando para siempre. */
  const DIALOG_BIND_INTERVAL_MS = 120;
  const DIALOG_BIND_ATTEMPTS = 25;
  const bindDialogWhenReady = () => {
    let left = DIALOG_BIND_ATTEMPTS;
    const tick = () => {
      observeForDialog();
      if (!codOpenInFlight) return;      // ya enlazó y soltó el candado
      if (--left > 0) { setTimeout(tick, DIALOG_BIND_INTERVAL_MS); return; }
      /*
        Se agotaron los intentos y el proveedor no montó nada: la apertura no
        llegó a ocurrir. Soltar el candado aquí en vez de esperar al
        temporizador de seguridad convierte «no pasa nada durante 6 s» en «el
        siguiente toque lo vuelve a intentar», que es lo que hace el cliente.
      */
      releaseCodOpenLock?.();
    };
    tick();
  };

  /* ------------------------------------------------------------------ *
   * Acciones
   * ------------------------------------------------------------------ */

  /*
    Llevar al buy box es la respuesta a "quería comprar y todavía no hay nada
    que comprar". Sin una señal visual, el cliente aterriza a mitad de página
    sin saber qué se supone que tiene que mirar: el scroll ya terminó cuando él
    todavía está buscando. La emphasis dura lo justo para dirigir la mirada y se
    retira sola; nada queda pegado ni cambia el estado de la oferta.

    Marca la escalera de packs si existe, porque es la decisión que se le está
    pidiendo. Si esa tienda no usa packs, el formulario entero es el ancla.
  */
  const ATTENTION_CLASS = 'is-master-attention';
  const ATTENTION_MS = 1800;
  let attentionTimer = null;

  const emphasizeOffer = () => {
    const form = productForm();
    if (!form) return;
    const target = form.querySelector('[data-pack-fieldset]') || form;
    clearTimeout(attentionTimer);
    document.querySelectorAll(`.${ATTENTION_CLASS}`).forEach(node => node.classList.remove(ATTENTION_CLASS));
    // Un reflow entre quitar y poner reinicia la animación cuando la CTA se
    // pulsa dos veces seguidas; sin él la segunda vez no se ve nada.
    void target.offsetWidth;
    target.classList.add(ATTENTION_CLASS);
    attentionTimer = setTimeout(() => target.classList.remove(ATTENTION_CLASS), ATTENTION_MS);
  };

  const scrollBuybox = () => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const form = productForm();
    form?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    emphasizeOffer();
    return { action: 'scroll_buybox', ok: Boolean(form) };
  };

  /*
    "Abrir carrito" desde una CTA de compra (flotante, sticky, respaldo de
    COD) no es lo mismo que pulsar el icono del carrito: quien pulsó el icono
    quiere VER su carrito, aunque esté vacío, pero quien pulsó "PEDIR AHORA"
    quería comprar, y un drawer vacío no es una respuesta a eso — es
    exactamente el bug que reportó el dueño. `data-item-count` en el drawer se
    mantiene sincronizado en cada render (ver master-cart.js), así que leerlo
    aquí es gratis y no requiere una petición nueva.
  */
  const openCart = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const knownEmpty = drawer && Number(drawer.dataset.itemCount || 0) === 0;
    if (knownEmpty) {
      document.dispatchEvent(new CustomEvent('master:cart-force-close'));
      return scrollBuybox();
    }
    if (window.MasterCart?.render) {
      // El drawer tiene dueño: se le presta el hook global en vez de abrirlo
      // desde aquí. Si no hay drawer, master-cart.js ya cae solo a /cart.
      document.querySelector('[data-cart-open]')?.click();
      if (drawer) return { action: 'open_cart', ok: true };
    }
    location.href = `${root}cart`;
    return { action: 'open_cart', ok: true };
  };

  /*
    Prepago. La regla es que llegue al checkout EXACTAMENTE el nivel elegido:
    quien selecciona 3 no puede aterrizar con 1. La forma de garantizarlo no es
    construir aquí un enlace de checkout —eso duplicaría la lógica de packs,
    bonus y código de nivel que ya vive en el carrito— sino usar el mismo envío
    del formulario que usa "añadir al carrito" y pedirle, sólo para esta vez,
    que termine en el checkout.

    El envío real siempre pasa por el FORMULARIO, nunca por el botón que
    resulte ser el principal en este momento: ese botón puede ser
    `type="button"` con `data-purchase-action="open_cod"` —exactamente lo que
    pasa cuando la CTA primaria es contraentrega— y en ese caso un `.click()`
    no somete nada: dispara la acción que ese botón declare, que es abrir el
    formulario COD, no el checkout de prepago. `requestSubmit` es el camino
    estándar; donde no existe, un evento `submit` sintético despachado sobre el
    propio formulario llega al mismo oyente delegado de master-cart.js sin
    depender de qué botón esté pintado como principal.
  */
  /*
    Un doble toque antes de que el primer envío navegue no debe iniciar un
    segundo /cart/add: el candado es local a esta acción —COD y las demás CTA
    no lo ven— y se libera solo, así que nunca deja un botón muerto.

    Corrección de Commit K: un temporizador fijo corto no es una garantía, es
    una apuesta. En una conexión móvil lenta el /cart/add real que dispara
    este mismo envío puede tardar más que cualquier plazo corto razonable, y
    entonces el candado se soltaría ANTES de que la primera petición termine
    —el mismo hueco doble-envío que este candado existe para cerrar—. La
    señal correcta ya existe: master-cart.js marca `document.documentElement`
    con la clase `master-cart-busy` durante exactamente lo que dura su propio
    /cart/add (setBusy/finally, ver master-cart.js), así que observar esa
    clase reutiliza el ciclo de vida real del envío en vez de adivinarlo con
    un reloj. El temporizador que queda es sólo un respaldo generoso para el
    caso límite en que esa señal nunca llegue —p. ej. si la validación nativa
    del formulario bloquea el submit antes de que master-cart.js lo vea—, no
    el mecanismo principal.

    Y como la navegación a /checkout puede volver por el back-forward cache
    con el candado congelado en "puesto" —la petición que lo iba a soltar
    quedó abandonada al navegar—, `pageshow` con `persisted` lo suelta
    explícitamente al volver: dejarlo puesto convertiría la CTA en un botón
    muerto para siempre.
  */
  const CHECKOUT_SAFETY_TIMEOUT_MS = 20000;
  let checkoutInFlight = false;
  let releaseCheckoutLock = null;

  const checkout = (options = {}) => {
    if (checkoutInFlight) return { action: 'native_checkout', ok: false, reason: 'ALREADY_IN_FLIGHT' };
    // El formulario COD está abriéndose: dejar que este checkout navegue
    // ahora mismo lo cortaría a mitad de apertura. Se rechaza mientras dura
    // esa ventana, no de forma permanente.
    if (codOpenInFlight) return { action: 'native_checkout', ok: false, reason: 'COD_OPENING' };

    const form = productForm();
    if (!form) return { action: 'native_checkout', ok: false, reason: 'NO_PRODUCT_FORM' };
    syncNativeProductState();
    form.dataset.afterAddOverride = 'checkout';

    /*
      Incentivo de prepago. El código lo declara la CTA que lo promete
      (`data-purchase-discount`), no este archivo: aquí sólo se transporta al
      único sitio que el carrito lee al añadir (`[data-pack-discount]`, ver
      master-cart.js), igual que se transporta la cantidad. Esta ruta es
      exclusiva del prepago porque sólo el prepago envía el formulario: la
      contraentrega abre el formulario del proveedor y nunca pasa por aquí, así
      que un descuento por pagar ahora no puede filtrarse a quien paga al
      recibir.

      Se restaura el valor anterior si el envío no llega a ocurrir; dejar el
      código puesto convertiría el siguiente "añadir al carrito" normal en uno
      con descuento que nadie prometió.
    */
    const discountField = form.querySelector('[data-pack-discount]');
    const promisedCode = (options.trigger?.dataset.purchaseDiscount || '').trim();
    const previousCode = discountField ? discountField.value : null;
    if (discountField && promisedCode) discountField.value = promisedCode;
    const restoreDiscount = () => {
      if (discountField && promisedCode && previousCode !== null) discountField.value = previousCode;
    };

    const trigger = options.trigger || null;
    trigger?.setAttribute('aria-busy', 'true');
    checkoutInFlight = true;

    let observer = null;
    let safetyTimer = null;
    const release = () => {
      if (!checkoutInFlight) return;
      checkoutInFlight = false;
      releaseCheckoutLock = null;
      trigger?.removeAttribute('aria-busy');
      observer?.disconnect();
      clearTimeout(safetyTimer);
      restoreDiscount();
    };
    releaseCheckoutLock = release;

    // El envío real deja la clase puesta mientras dura y la quita en su
    // `finally`, pase lo que pase con la petición; por eso basta con
    // reaccionar a que DEJE de estar puesta después de haber estado puesta,
    // sin depender de qué mutación exacta reporte el observer.
    let sawBusy = false;
    const root = document.documentElement;
    observer = new MutationObserver(() => {
      const busy = root.classList.contains('master-cart-busy');
      if (busy) sawBusy = true;
      else if (sawBusy) release();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    safetyTimer = setTimeout(release, CHECKOUT_SAFETY_TIMEOUT_MS);

    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));

    return { action: 'native_checkout', ok: true };
  };

  window.addEventListener('pageshow', event => {
    if (event.persisted) releaseCheckoutLock?.();
  });

  /*
    COD. El proveedor lee el formulario en el momento de abrir, así que la
    sincronización va antes del click y no después.
  */
  const openCod = (options = {}) => {
    // Dos toques casi simultáneos sobre la CTA de contraentrega no deben
    // pulsar el botón del proveedor dos veces; el segundo se rechaza en el
    // acto en vez de reintentar.
    if (codOpenInFlight) return { action: 'open_cod', ok: false, reason: 'ALREADY_IN_FLIGHT' };
    if (checkoutInFlight) return { action: 'open_cod', ok: false, reason: 'CHECKOUT_IN_FLIGHT' };

    /*
      Fuera de la ficha no hay formulario de producto, y el proveedor monta su
      botón DENTRO de ese formulario: en /cart cargado a pelo no existe ninguna
      puerta a la contraentrega en esta página. Antes eso devolvía
      NO_PRODUCT_FORM sin más y el botón no hacía nada — un callejón sin
      salida.

      La respuesta no puede ser el checkout de prepago (sería cobrar por
      adelantado a quien pidió pagar al recibir), así que se va a la ficha, que
      es donde esa puerta existe, llevando la cantidad para no aterrizar con el
      pack equivocado. Quien declara a qué ficha volver es la CTA, porque es la
      única que sabe qué producto hay en el carrito.
    */
    if (!productForm()) {
      const returnUrl = options.trigger?.dataset.purchaseCodUrl || '';
      if (returnUrl) {
        const separator = returnUrl.includes('?') ? '&' : '?';
        const qty = Math.max(1, Number(options.trigger?.dataset.purchaseCodQty || 1));
        location.href = `${returnUrl}${separator}${COD_RESUME_PARAM}=${qty}`;
        return { action: 'open_cod', ok: true, deferred: true };
      }
      return { action: 'open_cod', ok: false, reason: 'NO_PRODUCT_FORM' };
    }

    const sync = syncNativeProductState();
    if (!sync.ok) return { action: 'open_cod', ok: false, reason: sync.reason };

    const status = codStatus();
    if (status === 'READY') {
      codOpenInFlight = true;
      const safetyTimer = setTimeout(() => releaseCodOpenLock?.(), COD_OPEN_SAFETY_TIMEOUT_MS);
      releaseCodOpenLock = () => {
        if (!codOpenInFlight) return;
        codOpenInFlight = false;
        releaseCodOpenLock = null;
        clearTimeout(safetyTimer);
      };

      /*
        El formulario del proveedor es una capa modal, y el drawer también.
        Abrirlo sin cerrar el drawer deja las dos apiladas: se ve el carrito
        asomando por detrás del formulario de pedido y hay dos cierres
        compitiendo por el mismo gesto. El drawer tiene dueño, así que se le
        pide que se cierre por su propio canal en vez de tocarlo desde aquí;
        si no hay drawer, el evento no lo escucha nadie y no pasa nada.
      */
      document.dispatchEvent(new CustomEvent('master:cart-force-close'));

      if (provider.open()) {
        /*
          El <dialog> del proveedor todavía NO existe en este instante: se monta
          unos cientos de milisegundos después del click. Llamar aquí una sola
          vez a `observeForDialog()` no enlazaba nada —`watchLifecycle` salía por
          `!dialog`— y como el proveedor monta un nodo NUEVO en cada apertura,
          no había un segundo intento: el ciclo de vida se quedaba sin observar,
          el candado sólo caía por el temporizador de 6 s y durante esos 6 s
          ninguna CTA de compra respondía.

          Se reintenta acotado hasta que el nodo aparezca. En cuanto enlaza,
          `watchLifecycle` adopta su estado, libera el candado y vuelven a
          emitirse `master:cod-open` / `master:cod-close`.
        */
        bindDialogWhenReady();
        return { action: 'open_cod', ok: true, provider: provider.id, quantity: sync.quantity };
      }
      // El botón resultó no estar ahí pese a READY: no dejar el candado puesto
      // por una apertura que nunca empezó.
      releaseCodOpenLock();
    }

    // Nunca dejar un botón muerto: si el proveedor no está, la CTA sigue
    // llevando a algún sitio útil.
    const fallback = options.fallback || 'open_cart';
    const result = execute(fallback, { fallback: 'scroll_buybox', _fromFallback: true });
    return { action: 'open_cod', ok: false, reason: `COD_${status}`, fellBackTo: result.action };
  };

  const ACTIONS = {
    open_cod: openCod,
    native_checkout: checkout,
    open_cart: openCart,
    scroll_buybox: scrollBuybox
  };

  /*
    Segunda mitad del traspaso desde /cart: se llega a la ficha con la cantidad
    en la URL, se selecciona ese nivel y se abre el formulario COD.

    Se espera a que el proveedor esté READY en vez de abrir a ciegas: recién
    cargada la página su botón todavía no existe, y un click sobre nada sería
    un segundo callejón sin salida. El plazo es generoso y acotado; si expira,
    el cliente se queda en la ficha con su pack ya elegido, que es un sitio
    razonable donde estar.

    El parámetro se retira de la URL en cuanto se usa: recargar o compartir el
    enlace no debe reabrir el formulario.
  */
  const COD_RESUME_PARAM = 'master_cod';
  const COD_RESUME_TIMEOUT_MS = 8000;

  const resumeCod = () => {
    let params = null;
    try { params = new URLSearchParams(location.search); } catch (_) { return; }
    const raw = params.get(COD_RESUME_PARAM);
    if (!raw) return;

    params.delete(COD_RESUME_PARAM);
    const query = params.toString();
    history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);

    const form = productForm();
    if (!form) return;

    const wanted = Math.max(1, Number(raw) || 1);
    const card = [...form.querySelectorAll('[data-pack-card]')]
      .find(node => Number(node.dataset.packQty || 0) === wanted);
    const input = card?.querySelector('input');
    if (input && !input.checked) {
      input.checked = true;
      // El selector de packs escucha `change`; marcarlo a mano no lo dispara,
      // y sin él la cantidad del formulario no se actualizaría.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /*
      READY significa que el botón del proveedor está en el DOM, no que ya
      responda: recién cargada la página el botón existe unos cientos de
      milisegundos antes de que el proveedor le enganche su manejador, y un
      click en esa ventana se pierde en silencio —el traspaso terminaba en la
      ficha con el pack correcto pero sin formulario, que era el síntoma—.

      Por eso no basta con pulsar: se comprueba que el <dialog> se haya abierto
      de verdad y, si no, se vuelve a intentar. Sólo en este camino; un click
      del cliente no necesita reintentos porque para entonces el proveedor
      lleva rato listo.
    */
    const started = Date.now();
    const expired = () => Date.now() - started > COD_RESUME_TIMEOUT_MS;

    const attempt = () => {
      if (expired()) return;
      if (codStatus() !== 'READY') { setTimeout(attempt, 250); return; }
      openCod({ fallback: 'scroll_buybox' });
      setTimeout(() => {
        if (provider.isOpen() || expired()) return;
        releaseCodOpenLock?.();
        attempt();
      }, 600);
    };
    setTimeout(attempt, 400);
  };

  function execute(action, options = {}) {
    const run = ACTIONS[action];
    if (!run) {
      // Una acción desconocida es un error de configuración, no del cliente:
      // se comporta como el destino más inocuo en vez de no hacer nada.
      return options._fromFallback ? scrollBuybox() : scrollBuybox();
    }
    return run(options);
  }

  /* ------------------------------------------------------------------ *
   * Cableado de las CTA
   * ------------------------------------------------------------------ *
   *
   * Un solo listener delegado para las seis ubicaciones. Añadir una CTA nueva
   * en cualquier sección es poner `data-purchase-action` y nada más: no hay un
   * segundo sitio que tocar, que es justo lo que evita las seis integraciones
   * divergentes.
   */
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-purchase-action]');
    if (!trigger) return;
    const action = trigger.dataset.purchaseAction;
    if (!action || !ACTIONS[action]) return;
    event.preventDefault();
    execute(action, { fallback: trigger.dataset.purchaseFallback || 'open_cart', trigger });
  });

  /* ------------------------------------------------------------------ *
   * API pública
   * ------------------------------------------------------------------ */

  /*
    Contrato de rescate (§21-§23). MASTER tiene contextos de abandono
    INDEPENDIENTES —carrito, página, formulario COD— y fundirlos en uno solo
    sería perder información: quien cierra el carrito no está haciendo lo mismo
    que quien cierra el formulario de pedido.

    Lo que este objeto responde es sólo "¿procede molestar ahora?". La oferta en
    sí, su frecuencia y su código siguen viviendo en master-exit-save.js, que es
    el dueño del rescate y la única autoridad sobre lo que Shopify está
    cobrando de verdad.

    Importante y deliberado: NO existe un rescate CON DESCUENTO dentro del
    formulario COD. El proveedor no expone forma de inyectar un código en su
    formulario, y la única alternativa sería encender su campo público de cupón
    para todos los clientes. Prometer un 5 % que el formulario no puede aplicar
    sería exactamente la mentira que este theme no cuenta. Ver
    factory/v2/providers/releasit-capabilities.json.
  */
  const COD_COOLDOWN_MS = 4000;

  /*
    Exclusión mutua entre superficies de rescate. Hay más de una —el rescate de
    salida y la recuperación al cerrar el formulario COD— y las dos se disparan
    en momentos parecidos: dos modales a la vez son dos ventanas peleándose por
    la misma pantalla, no dos oportunidades.

    Se registran aquí en vez de mirarse entre ellas porque MasterPurchase ya es
    el sitio donde se decide "¿procede molestar ahora?": una tercera superficie
    futura sólo tiene que registrarse y hereda la regla, sin que las dos
    existentes tengan que enterarse de que existe.
  */
  const openRescues = new Set();
  const claimRescue = id => {
    if (openRescues.size > 0 && !openRescues.has(id)) return false;
    openRescues.add(id);
    return true;
  };
  const releaseRescue = id => openRescues.delete(id);

  const rescueContext = () => ({
    codOpen: provider.isOpen(),
    codCompleted: sessionFlag.get(),
    withinCodCooldown: lastClosedAt > 0 && (Date.now() - lastClosedAt) < COD_COOLDOWN_MS,
    rescueShowing: openRescues.size > 0,
    // Un modal encima del formulario de pedido, o justo al cerrarlo de un
    // toque accidental, es acoso, no rescate. Y uno encima de otro, también.
    allowRescue: !provider.isOpen()
      && !sessionFlag.get()
      && openRescues.size === 0
      && !(lastClosedAt > 0 && (Date.now() - lastClosedAt) < COD_COOLDOWN_MS)
  });

  window.MasterPurchase = {
    ACTIONS: Object.keys(ACTIONS),
    state,
    syncNativeProductState,
    codStatus,
    rescueContext,
    claimRescue,
    releaseRescue,
    isCodOpen: () => provider.isOpen(),
    openCod,
    openCart,
    checkout,
    execute,
    get provider() { return provider.id; },
    /* Punto de extensión para otro proveedor COD sin tocar las CTA ni las
       secciones. No se usa hoy; existe para que cambiar de proveedor sea
       reemplazar un objeto y no reescribir la ficha. */
    useProvider: next => { if (next && typeof next.open === 'function') { provider = next; watching = null; observeForDialog(); } }
  };

  /*
    Aviso sólo para el merchant dentro del editor. Declarar la app en los
    ajustes no la instala en ESTE tema: el App Embed se activa tema por tema.
    Cuando falta, la CTA sigue funcionando —cae al destino de respaldo— pero
    promete contraentrega y entrega otra cosa, y eso hay que decirlo antes de
    publicar, no después. Nunca se emite en la tienda.
  */
  const warnMerchant = () => {
    if (!window.Shopify?.designMode) return;
    if (!delegationActive()) return;
    if (codStatus() !== 'NOT_INSTALLED') return;
    console.warn(
      '[MASTER] Hay una CTA configurada como «Abrir formulario contraentrega», pero la app no está activa en este tema. ' +
      'Actívala en Personalizar → Incrustaciones de aplicación; el App Embed se activa tema por tema. ' +
      'Mientras tanto la CTA usa su destino de respaldo.'
    );
  };

  observeForDialog();
  resumeCod();
  document.addEventListener('shopify:section:load', observeForDialog);
  /* Tras el arranque del proveedor, no en el mismo tick: recién cargada la
     página aún no ha tenido ocasión de inyectar nada. */
  setTimeout(warnMerchant, 3000);
})();
